import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth-guard';
import { Login } from './features/auth/login/login';
import { NotFound } from './features/not-found/not-found';
import { Welcome } from './features/welcome/welcome';

export const routes: Routes = [
  {
    path: 'welcome',
    component: Welcome,
  },
  {
    path: 'login',
    component: Login,
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password').then(
        m => m.ForgotPassword
      ),
  },
  {
    path: '',
    loadComponent: () => import('./features/home/home').then(m => m.Home),
    canActivate: [authGuard],
    data: {
      title: 'Inicio',
      iconFilled: 'matHome',
      iconOutline: 'matHomeOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'vehicles/vehicles',
    loadChildren: () =>
      import('./features/vehicles/vehicles.routes').then(r => r.VehiclesRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Vehículos',
      iconFilled: 'matDirectionsCar',
      iconOutline: 'matDirectionsCarOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'vehicles/order',
    loadComponent: () =>
      import('./features/vehicles/vehicles-order/vehicles-order').then(
        m => m.VehiclesOrder
      ),
    canActivate: [authGuard],
    canDeactivate: [
      (component: { confirmLeave: () => Promise<boolean> }) =>
        component.confirmLeave(),
    ],
    data: {
      title: 'Marcas',
      iconFilled: 'matSwapVert',
      iconOutline: 'matSwapVertOutline',
      showInSidebar: true,
      permission: { resource: 'vehicles/order', action: 'view' },
    },
  },
  // Marcas se unificó en "Orden en la APP" (activar/ocultar y logo por marca).
  { path: 'vehicles/brands', redirectTo: 'vehicles/order', pathMatch: 'full' },
  {
    path: 'users',
    loadChildren: () =>
      import('./features/users/users.routes').then(r => r.UsersRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Usuarios',
      iconFilled: 'matPerson',
      iconOutline: 'matPersonOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'test-drive',
    loadChildren: () =>
      import('./features/test-drives/test-drives.routes').then(
        r => r.TestDrives
      ),
    canActivate: [authGuard],
    data: {
      title: 'Test Drive',
      iconFilled: 'phosphorSteeringWheelFill',
      iconOutline: 'phosphorSteeringWheel',
      showInSidebar: true,
    },
  },
  {
    path: 'plans',
    loadChildren: () =>
      import('./features/plans/plans.routes').then(r => r.PlansRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Planes',
      iconFilled: 'matWallet',
      iconOutline: 'matWalletOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'promotions',
    loadChildren: () =>
      import('./features/promotions/promotions.routes').then(r => r.Promotions),
    canActivate: [authGuard],
    data: {
      title: 'Promociones',
      iconFilled: 'matDiscount',
      iconOutline: 'matDiscountOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'logs/emaillogs',
    loadChildren: () =>
      import('./features/details/details.routes').then(r => r.DetailsRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Email Logs',
      iconFilled: 'matPendingActions',
      iconOutline: 'matPendingActionsOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'notifications',
    loadChildren: () =>
      import('./features/notifications/notifications.routes').then(
        r => r.NotificationsRoutes
      ),
    canActivate: [authGuard],
    data: {
      title: 'Notificaciones',
      iconFilled: 'matInfo',
      iconOutline: 'matInfoOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'vehicles/technical-colors',
    loadChildren: () =>
      import('./features/technical-colors/technical-colors.routes').then(
        r => r.TechnicalColors
      ),
    canActivate: [authGuard],
    data: {
      title: 'Colores Técnicos',
      iconFilled: 'matColorLens',
      iconOutline: 'matColorLensOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'logs/audilogs',
    loadChildren: () =>
      import('./features/audit-logs/audit-logs.routes').then(r => r.AuditLogs),
    canActivate: [authGuard],
    data: {
      title: 'Audit Logs',
      iconFilled: 'matAdminPanelSettings',
      iconOutline: 'matAdminPanelSettingsOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'logs/activitylogs',
    loadChildren: () =>
      import('./features/activity-logs/activity-logs.routes').then(r => r.ActivityLogs),
    canActivate: [authGuard],
    data: {
      title: 'Activity Logs',
      iconFilled: 'matCheckCircle',
      iconOutline: 'matCheckCircleOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'plans/payments-methods',
    loadChildren: () =>
      import('./features/payments-methods/payments-methods.routes').then(
        r => r.PaymentsMethodsRoutes
      ),
    canActivate: [authGuard],
    data: {
      title: 'Método de Pago',
      iconFilled: 'matPayment',
      iconOutline: 'matPaymentOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'tools',
    loadChildren: () =>
      import('./features/tools/tools.routes').then(r => r.ToolsRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Herramientas',
      iconFilled: 'matInfo',
      iconOutline: 'matInfoOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'permissions',
    loadChildren: () =>
      import('./features/permissions/permissions.routes').then(
        r => r.PermissionsRoutes
      ),
    canActivate: [authGuard],
    data: {
      title: 'Permisos',
      iconFilled: 'matAdminPanelSettings',
      iconOutline: 'matAdminPanelSettingsOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'security',
    loadChildren: () =>
      import('./features/security/security.routes').then(r => r.SecurityRoutes),
    canActivate: [authGuard],
    data: {
      title: 'Seguridad',
      iconFilled: 'matShield',
      iconOutline: 'matShieldOutline',
      showInSidebar: false,
    },
  },
  {
    path: 'plans/credit-payments',
    loadChildren: () =>
      import('./features/credit-payments/credit-payments.routes').then(
        r => r.CreditPaymentsRoutes
      ),
    canActivate: [authGuard],
    data: {
      title: 'Cobranza',
      iconFilled: 'matPayment',
      iconOutline: 'matPaymentOutline',
      showInSidebar: true,
    },
  },
  {
    path: 'legals',
    loadChildren: () =>
      import('./features/legal-documents/legal-documents.routes').then(
        r => r.LegalDocumentsRoutes
      ),
    canActivate: [authGuard],
    data: {
      title: 'Documentos Legales',
      iconFilled: 'matInsertDriveFile',
      iconOutline: 'matInsertDriveFileOutline', // Fallback to filled if outline not found, or maybe matStickyNote2Outline
      showInSidebar: true,
    },
  },
  {
    path: '**',
    component: NotFound,
  },
];

